import { useState } from 'react'

import * as XLSX from 'xlsx'
import { getYear, startOfMonth } from 'date-fns'
import ptBR from 'date-fns/locale/pt-BR'

// Material UI
import {
  ListItemText,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  RadioGroup,
  FormControlLabel,
  Radio,
  Typography,
  Grid
} from '@material-ui/core'

import {
  CloseTwoTone,
  CloudDownloadTwoTone,
  DateRange
} from '@material-ui/icons'

import { SheetButton, SheetModal } from 'views/Reports/components/styles'
import DateRangePickerWrapper from 'components/DateRanger/components/DateRangePickerWrapper'
import api from 'repositories/api'
import { useSelector } from 'react-redux'
import { safeDivision } from 'utils/format'
import { getConversionExportValues } from 'utils/conversion'
import { CircularProgress } from '@mui/material'
import { toast } from 'react-toastify'
import { deepClone } from 'utils/deepClone'
import { toIsoStringLocalTz } from 'utils/dateFunctions'
import { DateTime } from 'luxon'
import {
  formatGroupDataToExcel,
  formatOtherInputsStoreDataToExcel,
  roundSpreadsheetValue,
  formatSellerDataToExcel,
  formatStoreDataToExcel
} from './exportSpreadsheetFormatters'
import {
  getClosedMonthExportRange,
  getExportMonths
} from './exportSpreadsheetDates'
import { normalizeExportResponse } from './exportSpreadsheetResponse'
import {
  appendAllStoresRows,
  createAllStoresRows
} from './exportSpreadsheetRows'
import {
  getSalesDataDisabledReason,
  getReportExportValue,
  isFieldDisabled
} from 'utils/reportDisabledFields'

const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: 300,
      width: 150
    }
  },
  anchorOrigin: {
    vertical: 'bottom',
    horizontal: 'left'
  },
  transformOrigin: {
    vertical: 'top',
    horizontal: 'left'
  },
  getContentAnchorEl: null
}

export const isDetailedExportRangeValid = range =>
  range.from.getMonth() === range.to.getMonth() &&
  range.from.getFullYear() === range.to.getFullYear()

export const ExportSpreadSheetModal = ({ isOpen, onClose }) => {
  const { user } = useSelector(state => state.user)

  const [selectedMonth, setSelectedMonth] = useState('')
  const [selectionType, setSelectionType] = useState('month')
  const [range, setRange] = useState({ from: null, to: null })
  const [isCalendarPickerOpen, setIsCalendarPickerOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  const currentMonth = new Date().setHours(0, 0, 0, 0)

  const months = getExportMonths(currentMonth)

  const getMonthName = stringDate => {
    const dateMonth = new Date(stringDate).toLocaleDateString('pt-BR', {
      month: 'long'
    })

    return `${dateMonth[0].toUpperCase()}${dateMonth.slice(1)}`
  }

  const closeModal = () => {
    setSelectedMonth('')
    onClose()
  }

  const handleExportAllSpreadSheet = async () => {
    const isMonthSelection = selectionType === 'month'
    if (isMonthSelection && !selectedMonth) {
      toast.error('Selecione o mês que quer exportar')
      return
    }
    if (!isMonthSelection && (!range.from || !range.to)) {
      toast.error('Selecione o período que quer exportar')
      return
    }
    if (
      !isMonthSelection &&
      !isDetailedExportRangeValid(range)
    ) {
      toast.error('A exportação detalhada deve usar datas do mesmo mês')
      return
    }

    try {
      setIsLoading(true)
      const { start, end } = isMonthSelection
        ? getClosedMonthExportRange(selectedMonth)
        : { start: range.from, end: range.to }

      const startMonth = toIsoStringLocalTz(start)
      const endMonth = toIsoStringLocalTz(end)
      const response = await api.axios.get(
        `report/user/${user._id}/export?from=${startMonth}&to=${endMonth}`,
          {
            timeout: 160000
          }
        )

        const {
          stores: storesReportData,
          groups: groupsReportData,
          disabledFields
        } = normalizeExportResponse(response.data)
        const salesDataReason = getSalesDataDisabledReason(disabledFields)
        const salesDataDisabled = isFieldDisabled(salesDataReason)
        const periodLabel = `${DateTime.fromJSDate(start).toFormat(
          'dd/MM/yyyy'
        )} até ${DateTime.fromJSDate(end).toFormat('dd/MM/yyyy')}`

        const tableHeaders = [
          'Nome',
          'CPF',
          'Faturamento',
          'Projeção de Faturamento',
          'Meta Acumulada',
          '% da Meta',
          'Meta do Mês',
          'Meta Atual',
          'Dias Trabalhados',
          'Piso',
          'Complemento',
          '% de Comissão Base',
          'Comissão Base',
          '% de Comissão',
          'Comissão',
          'DSR',
          'Valor DSR',
          'Bônus',
          'Valor Bônus',
          'Remuneração Total',
          'Projeção de Remuneração',
          'N˚ Vendas',
          'Meta Vendas',
          'Ticket Médio',
          'Meta TM',
          'Peça/Venda',
          'Meta PA',
          'Preço Médio',
          'Meta PM',
          'N˚ Peças',
          'Meta Peças'
        ]

        const groupTableHeaders = [
          'Regional',
          'Faturamento',
          'Projeção de Faturamento',
          'Meta Acumulada',
          '% da Meta',
          'Meta do Mês',
          'N˚ Vendas',
          'Meta Vendas',
          'Ticket Médio',
          'Meta TM',
          'Peça/Venda',
          'Meta PA',
          'Preço Médio',
          'Meta PM',
          'N˚ Peças',
          'Meta Peças'
        ]

        const wb = XLSX.utils.book_new()

        const reservedNames = []
        const allStoresTabSheet = createAllStoresRows(tableHeaders)
        // Pessoas e Conversão vão no fim da aba "Todas as lojas", só na linha
        // da loja, e só quando alguma loja tem contagem de pessoas.
        const hasConversion = storesReportData.some(store => store.conversion)
        const salesColumnIndex = tableHeaders.indexOf('N˚ Vendas') + 1
        if (hasConversion) allStoresTabSheet[0].push('Pessoas', 'Conversão')

        let sellersCount = 0
        let storesCount = 0
        let othersCount = 0
        let indexCount = 0
        const getRemovableCommissionColumns = () => [
          ...(!disabledFields.commission
            ? [
                'Piso',
                'Complemento',
                '% de Comissão Base',
                'Comissão Base',
                '% de Comissão',
                'Comissão',
                'DSR',
                'Valor DSR',
                'Bônus',
                'Valor Bônus',
                'Remuneração Total'
              ]
            : []),
          ...(!disabledFields.projection?.commission
            ? ['Projeção de Remuneração']
            : [])
        ]
        let columnsToRemoveAllStores = getRemovableCommissionColumns()

        const fillObjectGaps = ws => {
          const getExcelColumnName = n => {
            let s = ''
            while (n >= 0) {
              s = String.fromCharCode((n % 26) + 65) + s
              n = Math.floor(n / 26) - 1
            }
            return s
          }

          const obj = { ...ws }
          const ref = ws['!ref']

          delete obj['!ref']

          const keys = Object.keys(obj)
          const groupedKeys = {}

          keys.forEach(key => {
            const match = key.match(/^([A-Z]+)([0-9]+)$/)
            if (match) {
              const letter = match[1]
              const number = parseInt(match[2])

              if (!groupedKeys[number]) {
                groupedKeys[number] = []
              }
              groupedKeys[number].push(letter)
            }
          })

          // Step 2: Organiza os números e letras
          const sortedGroups = Object.entries(groupedKeys).sort(
            ([num1], [num2]) => num1 - num2 // Ordena pelo número das colunas
          )

          // Step 3: Cria um novo objeto sem gaps
          const newObject = {}
          sortedGroups.forEach(([number, letters]) => {
            letters.sort((a, b) => a.length - b.length || a.localeCompare(b))
            letters.forEach((_, index) => {
              const newLetter = getExcelColumnName(index)
              const oldKey = `${_}${number}` // Key original (ex., A1, B2, etc.)
              const newKey = `${newLetter}${number}` // Nova key com letras sequenciais, mas com o mesmo número
              newObject[newKey] = obj[oldKey] //Move o objeto pra nova key
            })
          })

          return {
            ['!ref']: ref,
            ...newObject
          }
        }

        storesReportData.forEach(
          ({ name, store, sellers, otherInputs, other, hasInputs, conversion }, index) => {
            // Caso seja loja Oscar
            let oscarTableStoreData = []
            let oscarTableUsersData = []
            let columnsToRemove = getRemovableCommissionColumns()

            const checkColumnsToRemove = seller => {
              const {
                complement,
                dsr,
                dsrDays,
                salary,
                bonus,
                fixedCommission,
                commission,
                sumCommission,
                sumSectionsCommissions
              } = seller.commission || {}

              const conditionToRemoveColumnItemsMap = {
                Piso: !!salary,
                Complemento: !!complement,
                '% de Comissão Base': !!fixedCommission?.percentage,
                'Comissão Base': !!fixedCommission?.value,
                '% de Comissão': !!commission?.percentage,
                Comissão: !!commission?.value,
                DSR: !!dsrDays,
                'Valor DSR': !!dsr,
                Bônus: !!bonus?.percentage,
                'Valor Bônus': !!bonus?.value,
                'Remuneração Total': !!(
                  sumCommission || sumSectionsCommissions
                ),
                'Projeção de Remuneração':
                  !!(
                    seller.projection?.commission?.sumCommission ||
                    seller.projection?.commission?.sumSectionsCommissions
                  )
              }

              const aux = []
              // Filter out columns where the condition is true
              columnsToRemove.forEach(column => {
                if (conditionToRemoveColumnItemsMap[column]) {
                  aux.push(column)
                }
              })

              columnsToRemove = columnsToRemove.filter(
                col => !aux.includes(col)
              )
              aux.splice(0)

              // Filter out columns where the condition is true (all stores)
              columnsToRemoveAllStores.forEach(column => {
                if (conditionToRemoveColumnItemsMap[column]) {
                  aux.push(column)
                }
              })

              columnsToRemoveAllStores = columnsToRemoveAllStores.filter(
                col => !aux.includes(col)
              )
              aux.splice(0)
            }

            let sellersOtherInputsCount = 0

            let formattedName = name

            if (reservedNames.includes(name)) {
              formattedName = name.substring(0, 31 - 16) + '(nome duplicado)'
            } else if (
              name.length > 31 &&
              reservedNames.find(
                reservedName =>
                  reservedName.substring(0, 31 - 3) + '...' ===
                  name.substring(0, 31 - 3) + '...'
              )
            ) {
              const subNumber = (indexCount + 1).toString().length
              formattedName =
                name.substring(0, 31 - 5 - subNumber) + `...(${indexCount + 1})`
              indexCount += 1
            } else {
              if (name.length > 31) {
                formattedName = name.substring(0, 31 - 3) + '...'
              }
            }

            reservedNames.push(name)

            if (store) {
              sellersCount += sellers.length
              storesCount++

              const tableStoreData = formatStoreDataToExcel(
                store,
                disabledFields,
                { hasInputs: hasInputs !== false }
              )

              // Tabela para cada loja das Lojas Oscar
              if (otherInputs) {
                const formattedOscarStoreTable =
                  formatOtherInputsStoreDataToExcel(
                    store,
                    otherInputs,
                    disabledFields,
                    { hasInputs: hasInputs !== false }
                  )

                // Remoção de alguns dados das colunas da loja das Lojas Oscar
                // % da Meta / Meta Atual / Comissão / Projeção de Comissão
                // const indexColumnsToBeModified = [4, 5, 7, 8]

                // for (let i = 0; i < indexColumnsToBeModified.length; i++) {
                //   formattedOscarStoreTable.splice(
                //     indexColumnsToBeModified[i],
                //     1,
                //     '-'
                //   )
                // }
                oscarTableStoreData = formattedOscarStoreTable
              }

              const formatSections = sections => {
                const sellerData = sections.reduce((acc, curr, index) => {
                  const {
                    salary,
                    complement,
                    fixedCommission,
                    commission,
                    dsrDays,
                    dsr,
                    bonus,
                    sumCommission
                  } = curr

                  // Check and update salary
                  if (!!salary) {
                    acc.salary ? (acc.salary += salary) : (acc.salary = salary)
                  }

                  // Check and update complement
                  if (!!complement) {
                    acc.complement
                      ? (acc.complement += complement)
                      : (acc.complement = complement)
                  }

                  // Check and update fixed commission value
                  if (!!fixedCommission?.value) {
                    acc.fixedCommission = acc.fixedCommission || {}
                    acc.fixedCommission.value
                      ? (acc.fixedCommission.value += fixedCommission.value)
                      : (acc.fixedCommission.value = fixedCommission.value)
                  }

                  // Check and update fixed commission percentage
                  if (!!fixedCommission?.percentage) {
                    acc.fixedCommission = acc.fixedCommission || {}
                    const periodString = `Período ${index + 1} (${(
                      fixedCommission.percentage * 100
                    ).toFixed(2)}%)`
                    acc.fixedCommission.percentage
                      ? (acc.fixedCommission.percentage += ` / ${periodString}`)
                      : (acc.fixedCommission.percentage = periodString)
                  }

                  // Check and update commission value
                  if (!!commission?.value) {
                    acc.commission = acc.commission || {}
                    acc.commission.value
                      ? (acc.commission.value += commission.value)
                      : (acc.commission.value = commission.value)
                  }

                  // Check and update commission percentage
                  if (!!commission?.percentage) {
                    acc.commission = acc.commission || {}
                    acc.commission.percentage
                      ? (acc.commission.percentage += commission.percentage)
                      : (acc.commission.percentage = commission.percentage)
                  }

                  // Check and update DSR days
                  if (!!dsrDays) {
                    acc.dsrDays
                      ? (acc.dsrDays += dsrDays)
                      : (acc.dsrDays = dsrDays)
                  }

                  // Check and update DSR value
                  if (!!dsr) {
                    acc.dsr ? (acc.dsr += dsr) : (acc.dsr = dsr)
                  }

                  // Check and update bonus value
                  if (!!bonus?.value) {
                    acc.bonus = acc.bonus || {}
                    acc.bonus.value
                      ? (acc.bonus.value += bonus.value)
                      : (acc.bonus.value = bonus.value)
                  }

                  // Check and update bonus percentage
                  if (!!bonus?.percentage) {
                    acc.bonus = acc.bonus || {}
                    acc.bonus.percentage
                      ? (acc.bonus.percentage += bonus.percentage)
                      : (acc.bonus.percentage = bonus.percentage)
                  }

                  // Check and update total remuneration
                  if (!!sumCommission) {
                    acc.sumSectionsCommissions
                      ? (acc.sumSectionsCommissions += sumCommission)
                      : (acc.sumSectionsCommissions = sumCommission)
                  }

                  return acc
                }, {})

                return sellerData
              }

              const formatCommissionSections = commissionData =>
                commissionData?.sections
                  ? {
                      ...formatSections(commissionData.sections),
                      sumSectionsCommissions:
                        commissionData.sumSectionsCommissions ??
                        commissionData.sections.reduce(
                          (total, section) =>
                            total + (section.sumCommission || 0),
                          0
                        )
                    }
                  : commissionData

              const tableUsersData = sellers.map(rawSeller => {
                const seller = {
                  ...rawSeller,
                  commission: isFieldDisabled(disabledFields.commission)
                    ? rawSeller.commission
                    : formatCommissionSections(rawSeller.commission),
                  projection: {
                    ...rawSeller.projection,
                    commission: isFieldDisabled(
                      disabledFields.projection?.commission
                    )
                      ? rawSeller.projection?.commission
                      : formatCommissionSections(
                        rawSeller.projection?.commission
                      )
                  }
                }

                // Tabela para cada vendedor das Lojas Oscar
                if (seller.otherInputs) {
                  const formattedSellerOscarStoreTable =
                    formatSellerDataToExcel(
                      {
                        ...seller,
                        total: { ...seller.otherInputs }
                      },
                      disabledFields,
                      { hasInputs: seller.hasInputs !== false }
                    )

                  // Remoção de alguns dados das colunas de vendedores das Lojas Oscar
                  // Meta Atual / Comissão / Projeção de Comissão
                  // const indexColumnsToBeModified = [6, 7, 8]

                  // for (let i = 0; i < indexColumnsToBeModified.length; i++) {
                  //   formattedSellerOscarStoreTable.splice(
                  //     indexColumnsToBeModified[i],
                  //     1,
                  //     '-'
                  //   )
                  // }

                  oscarTableUsersData.push(formattedSellerOscarStoreTable)
                  sellersOtherInputsCount++
                }

                // Tabela de lojas comuns
                if (columnsToRemove.length > 0) {
                  checkColumnsToRemove(seller)
                }
                return formatSellerDataToExcel(seller, disabledFields, {
                  hasInputs: seller.hasInputs !== false
                })
              })

              // Adiciona nome da loja nas colunas loja e vendedores (na tab Todas as lojas)
              const allStoresTableUserData = deepClone(tableUsersData)
              allStoresTableUserData.forEach(userRow =>
                userRow.splice(0, 0, name)
              )

              const allStoresTableStoreData = deepClone(tableStoreData)
              allStoresTableStoreData.splice(0, 0, name)
              if (hasConversion) {
                allStoresTableStoreData.push(
                  ...getConversionExportValues(
                    conversion && {
                      sales: allStoresTableStoreData[salesColumnIndex],
                      visitors: conversion.visitors
                    },
                    salesDataReason
                  )
                )
              }

              const allStoresColumn = [
                allStoresTableStoreData,
                ...allStoresTableUserData
              ]

              const defaultColumnsExcel = [
                [name],
                [periodLabel],
                tableHeaders,
                tableStoreData,
                ...tableUsersData
              ]

              if (other && other?.sales > 0) {
                defaultColumnsExcel.push([
                  'Outras vendas',
                  '-', // CPF
                  getReportExportValue(salesDataReason, other.sold ?? 0), // Faturamento
                  '-', // Projeção de Faturamento
                  '-', // Meta Principal
                  '-', // % da Meta
                  '-', // Meta do mês
                  '-', // Meta Atual
                  '-', // Dias trabalhados
                  '-', // Piso
                  '-', // Complemento
                  '-', // % de Comissão Base
                  '-', // Comissão Base
                  '-', // % de Comissão
                  '-', // Comissão
                  '-', // DSR
                  '-', // Valor DSR
                  '-', // Bônus
                  '-', // Valor Bônus
                  '-', // Remuneração Total
                  '-', // Projeção de Remuneração
                  getReportExportValue(salesDataReason, other.sales ?? 0), // N˚ Vendas
                  '-', // Meta Vendas
                  hasInputs === false || salesDataDisabled
                    ? '-'
                    : safeDivision(other.sold, other.sales), // Ticket Médio
                  '-', // Meta TM
                  hasInputs === false || salesDataDisabled
                    ? '-'
                    : safeDivision(other.items, other.sales), // Peça/Venda
                  '-', // Meta PA
                  hasInputs === false || salesDataDisabled
                    ? '-'
                    : safeDivision(other.sold, other.items), // Preço Médio
                  '-', // Meta PM
                  getReportExportValue(salesDataReason, other.items ?? 0), // N˚ Peças
                  '-' // Meta Peças
                ])

                allStoresColumn.push([
                  name, //
                  'Outras vendas',
                  '-', // CPF
                  getReportExportValue(salesDataReason, other.sold ?? 0), // Faturamento
                  '-', // Projeção de Faturamento
                  '-', // Meta Principal
                  '-', // % da Meta
                  '-', // Meta do mês
                  '-', // Meta Atual
                  '-', // Dias trabalhados
                  '-', // Piso
                  '-', // Complemento
                  '-', // % de Comissão Base
                  '-', // Comissão Base
                  '-', // % de Comissão
                  '-', // Comissão
                  '-', // DSR
                  '-', // Valor DSR
                  '-', // Bônus
                  '-', // Valor Bônus
                  '-', // Remuneração Total
                  '-', // Projeção de Remuneração
                  getReportExportValue(salesDataReason, other.sales ?? 0), // N˚ Vendas
                  '-', // Meta Vendas
                  hasInputs === false || salesDataDisabled
                    ? '-'
                    : safeDivision(other.sold, other.sales), // Ticket Médio
                  '-', // Meta TM
                  hasInputs === false || salesDataDisabled
                    ? '-'
                    : safeDivision(other.items, other.sales), // Peça/Venda
                  '-', // Meta PA
                  hasInputs === false || salesDataDisabled
                    ? '-'
                    : safeDivision(other.sold, other.items), // Preço Médio
                  '-', // Meta PM
                  getReportExportValue(salesDataReason, other.items ?? 0), // N˚ Peças
                  '-' // Meta Peças
                ])
              }

              if (otherInputs) {
                defaultColumnsExcel.splice(2, 0, [
                  'Desempenho vendedores - Todas as Lojas'
                ])

                defaultColumnsExcel.push(
                  [],
                  [`Desempenho Loja - ${name}`],
                  tableHeaders,
                  oscarTableStoreData,
                  ...oscarTableUsersData
                )
              }

              appendAllStoresRows(allStoresTabSheet, allStoresColumn)

              const ws = XLSX.utils.aoa_to_sheet(defaultColumnsExcel)

              const indexSum = otherInputs ? 5 : 4
              // Lojas normais
              othersCount += other?.sales > 0 ? 1 : 0
              new Array(sellers.length + 1 + (other?.sales > 0 ? 1 : 0))
                .fill(0)
                .forEach((_, index) => {
                  ws[`C${index + indexSum}`].z = '"R$ "#,##0.00' // Faturamento
                  ws[`D${index + indexSum}`].z = '"R$ "#,##0.00' // Projeção de Faturamento
                  ws[`E${index + indexSum}`].z = '"R$ "#,##0.00' // Meta Principal
                  ws[`F${index + indexSum}`].z = '0.00%' // % da Meta
                  ws[`G${index + indexSum}`].z = '"R$ "#,##0.00' // Piso
                  // H Meta Atual
                  ws[`I${index + indexSum}`].z = '0' // Dias trabalhado
                  ws[`J${index + indexSum}`].z = '"R$ "#,##0.00' // Piso
                  ws[`K${index + indexSum}`].z = '"R$ "#,##0.00' // Complemento
                  ws[`L${index + indexSum}`].z = '0.00%' // % Comissão Base
                  ws[`M${index + indexSum}`].z = '"R$ "#,##0.00' // Comissão Base
                  ws[`N${index + indexSum}`].z = '0.00%' // % de Comissão
                  ws[`O${index + indexSum}`].z = '"R$ "#,##0.00' // Comissão
                  ws[`P${index + indexSum}`].z = '0' // DSR
                  ws[`Q${index + indexSum}`].z = '"R$ "#,##0.00' // Valor DSR
                  ws[`R${index + indexSum}`].z = '0.00%' // Bônus
                  ws[`S${index + indexSum}`].z = '"R$ "#,##0.00' // Valor Bônus
                  ws[`T${index + indexSum}`].z = '"R$ "#,##0.00' // Remuneração Total
                  ws[`U${index + indexSum}`].z = '"R$ "#,##0.00' // Projeção Remuneração
                  ws[`V${index + indexSum}`].z = '0' // Nº de vendas
                  ws[`W${index + indexSum}`].z = '0' // Meta Vendas
                  ws[`X${index + indexSum}`].z = '"R$ "#,##0.00' // Ticket médio
                  ws[`Y${index + indexSum}`].z = '"R$ "#,##0.00' // Meta TM
                  ws[`Z${index + indexSum}`].z = '0.00' // Peça/Venda
                  ws[`AA${index + indexSum}`].z = '0.00' // Meta PA
                  if (ws[`AB${index + indexSum}`])
                    ws[`AB${index + indexSum}`].z = '"R$ "#,##0.00' // Preço médio
                  if (ws[`AC${index + indexSum}`])
                    ws[`AC${index + indexSum}`].z = '"R$ "#,##0.00' // Meta PM
                  if (ws[`AD${index + indexSum}`])
                    ws[`AD${index + indexSum}`].z = '0' // Nº de Peças
                  if (ws[`AE${index + indexSum}`])
                    ws[`AE${index + indexSum}`].z = '0' // Meta Peças
                })

              if (otherInputs) {
                new Array(sellersOtherInputsCount + 1)
                  .fill(0)
                  .forEach((_, index) => {
                    // other inputs
                    ws[`C${index + 9 + sellers.length}`].z = '"R$ "#,##0.00' // Faturamento
                    ws[`D${index + 9 + sellers.length}`].z = '"R$ "#,##0.00' // Projeção de Faturamento
                    ws[`E${index + 9 + sellers.length}`].z = '"R$ "#,##0.00' // Meta Principal
                    ws[`F${index + 9 + sellers.length}`].z = '0.00%' // % da Meta
                    ws[`G${index + 9 + sellers.length}`].z = '"R$ "#,##0.00' // Meta do Mês
                    // H Meta Atual
                    ws[`I${index + 9 + sellers.length}`].z = '0' // Dias trabalhados
                    ws[`J${index + 9 + sellers.length}`].z = '"R$ "#,##0.00' // Piso
                    ws[`K${index + 9 + sellers.length}`].z = '"R$ "#,##0.00' // Complemento
                    ws[`L${index + 9 + sellers.length}`].z = '0.00%' // % Comissão Base
                    ws[`M${index + 9 + sellers.length}`].z = '"R$ "#,##0.00' // Comissão Base
                    ws[`N${index + 9 + sellers.length}`].z = '0.00%' // % de Comissão
                    ws[`O${index + 9 + sellers.length}`].z = '"R$ "#,##0.00' // Comissão
                    ws[`P${index + 9 + sellers.length}`].z = '0' // DSR
                    ws[`Q${index + 9 + sellers.length}`].z = '"R$ "#,##0.00' // Valor DSR
                    ws[`R${index + 9 + sellers.length}`].z = '0.00%' // Bônus
                    ws[`S${index + 9 + sellers.length}`].z = '"R$ "#,##0.00' // Valor Bônus
                    ws[`T${index + 9 + sellers.length}`].z = '"R$ "#,##0.00' // Remuneração Total
                    ws[`U${index + 9 + sellers.length}`].z = '"R$ "#,##0.00' // Projeção Remuneração
                    ws[`V${index + 9 + sellers.length}`].z = '0' // Nº de vendas
                    ws[`W${index + 9 + sellers.length}`].z = '0' // Meta Vendas
                    ws[`X${index + 9 + sellers.length}`].z = '"R$ "#,##0.00' // Ticket médio
                    ws[`Y${index + 9 + sellers.length}`].z = '"R$ "#,##0.00' // Meta TM
                    ws[`Z${index + 9 + sellers.length}`].z = '0.00' // Peça/Venda
                    ws[`AA${index + 9 + sellers.length}`].z = '0.00' // Meta PA
                    if (ws[`AB${index + 9 + sellers.length}`])
                      ws[`AB${index + 9 + sellers.length}`].z = '"R$ "#,##0.00' // Preço médio
                    if (ws[`AC${index + 9 + sellers.length}`])
                      ws[`AC${index + 9 + sellers.length}`].z = '"R$ "#,##0.00' // Meta PM
                    if (ws[`AD${index + 9 + sellers.length}`])
                      ws[`AD${index + 9 + sellers.length}`].z = '0' // Nº de Peças
                    if (ws[`AE${index + 9 + sellers.length}`])
                      ws[`AE${index + 9 + sellers.length}`].z = '0' // Meta Peças
                  })
              }

              if (columnsToRemove.length > 0) {
                const columnsToRemoveIndex = columnsToRemove.map(col =>
                  tableHeaders.findIndex(header => header === col)
                )
                const columnsLetter = columnsToRemoveIndex.map(col =>
                  String.fromCharCode(col + 1 + 64)
                )

                Object.entries(ws).forEach(([key]) => {
                  const column = key[0]

                  if (columnsLetter.includes(column)) {
                    delete ws[key]
                  }
                })
              }

              const newWs = fillObjectGaps(ws)

              XLSX.utils.book_append_sheet(wb, newWs, formattedName)
            } else {
              const ws = XLSX.utils.aoa_to_sheet([
                [name.toUpperCase()],
                [periodLabel],
                ['Sem meta cadastrada para esse mês.']
              ])
              XLSX.utils.book_append_sheet(wb, ws, formattedName)
            }
          }
        )
        const otherSales = storesReportData.reduce(
          (acc, curr) => {
            acc.sales += curr.other?.sales || 0
            acc.items += curr.other?.items || 0
            acc.sold += curr.other?.sold || 0
            return acc
          },
          {
            sales: 0,
            items: 0,
            sold: 0
          }
        )

        const ws = XLSX.utils.aoa_to_sheet(allStoresTabSheet)

        new Array(sellersCount + storesCount + 1 + othersCount)
          .fill(0)
          .forEach((_, index) => {
            ws[`D${index + 1}`].z = '"R$ "#,##0.00' // Faturamento
            ws[`E${index + 1}`].z = '"R$ "#,##0.00' // Projeção de Faturamento
            ws[`F${index + 1}`].z = '"R$ "#,##0.00' // Meta Principal
            ws[`G${index + 1}`].z = '0.00%' // % da Meta
            ws[`H${index + 1}`].z = '"R$ "#,##0.00' // Meta do mês
            //  I Meta Atual
            ws[`J${index + 1}`].z = '0' // Dias trabalhados
            ws[`K${index + 1}`].z = '"R$ "#,##0.00' // Piso

            ws[`L${index + 1}`].z = '"R$ "#,##0.00' // Complemento
            ws[`M${index + 1}`].z = '0.00%' // % Comissão Base
            ws[`N${index + 1}`].z = '"R$ "#,##0.00' // Comissão Base
            ws[`O${index + 1}`].z = '0.00%' // % de Comissão
            ws[`P${index + 1}`].z = '"R$ "#,##0.00' // Comissão
            ws[`Q${index + 1}`].z = '0' // DSR
            ws[`R${index + 1}`].z = '"R$ "#,##0.00' // Valor DSR
            ws[`S${index + 1}`].z = '0.00%' // Bônus
            ws[`T${index + 1}`].z = '"R$ "#,##0.00' // Valor Bônus
            ws[`U${index + 1}`].z = '"R$ "#,##0.00' // Remuneração Total
            ws[`V${index + 1}`].z = '"R$ "#,##0.00' // Projeção Remuneração
            ws[`W${index + 1}`].z = '0' // Nº de vendas
            ws[`X${index + 1}`].z = '0' // Meta Vendas
            ws[`Y${index + 1}`].z = '"R$ "#,##0.00' // Ticket médio
            ws[`Z${index + 1}`].z = '"R$ "#,##0.00' // Meta TM
            ws[`AA${index + 1}`].z = '0.00' // Peça/Venda
            if (ws[`AB${index + 1}`]) ws[`AB${index + 1}`].z = '0.00' // Meta PA
            if (ws[`AC${index + 1}`]) ws[`AC${index + 1}`].z = '"R$ "#,##0.00' // Preço médio
            if (ws[`AD${index + 1}`]) ws[`AD${index + 1}`].z = '"R$ "#,##0.00' // Meta PM
            if (ws[`AE${index + 1}`]) ws[`AE${index + 1}`].z = '0' // Nº de Peças
            if (ws[`AF${index + 1}`]) ws[`AF${index + 1}`].z = '0' // Meta Peças
            if (ws[`AG${index + 1}`]) ws[`AG${index + 1}`].z = '0' // Pessoas
            if (ws[`AH${index + 1}`]) ws[`AH${index + 1}`].z = '0.00%' // Conversão
          })

        if (columnsToRemoveAllStores.length > 0) {
          const columnsToRemoveIndex = columnsToRemoveAllStores.map(col =>
            tableHeaders.findIndex(header => header === col)
          )
          const columnsLetter = columnsToRemoveIndex.map(col =>
            String.fromCharCode(col + 2 + 64)
          )

          Object.entries(ws).forEach(([key]) => {
            const column = key[0]

            if (columnsLetter.includes(column)) {
              delete ws[key]
            }
          })
        }

        const newWs = fillObjectGaps(ws)

        XLSX.utils.book_append_sheet(wb, newWs, 'Todas as lojas')

        // Removemos a tab 'Todas as lojas' que está no final pra poder adicionar no inicio das tabs
        const allStoresColumnIndex = wb.SheetNames.indexOf('Todas as lojas')
        wb.SheetNames.splice(allStoresColumnIndex, 1)
        wb.SheetNames.unshift('Todas as lojas')

        if (groupsReportData.length > 0) {
          const groupsSheetData = [
            groupTableHeaders,
            ...groupsReportData.map(group =>
              formatGroupDataToExcel(group, disabledFields, {
                hasInputs: group.hasInputs !== false
              })
            )
          ]
          const groupsSheet = XLSX.utils.aoa_to_sheet(groupsSheetData)
          const groupsSheetColumnFormats = {
            B: '"R$ "#,##0.00',
            C: '"R$ "#,##0.00',
            D: '"R$ "#,##0.00',
            E: '0.00%',
            F: '"R$ "#,##0.00',
            G: '0',
            H: '0',
            I: '"R$ "#,##0.00',
            J: '"R$ "#,##0.00',
            K: '0.00',
            L: '0.00',
            M: '"R$ "#,##0.00',
            N: '"R$ "#,##0.00',
            O: '0',
            P: '0'
          }

          new Array(groupsReportData.length + 1).fill(0).forEach((_, index) => {
            Object.entries(groupsSheetColumnFormats).forEach(([column, format]) => {
              groupsSheet[`${column}${index + 1}`].z = format
            })
          })

          XLSX.utils.book_append_sheet(wb, groupsSheet, 'Regionais')

          const groupsSheetIndex = wb.SheetNames.indexOf('Regionais')
          wb.SheetNames.splice(groupsSheetIndex, 1)
          wb.SheetNames.splice(1, 0, 'Regionais')
        }

        const companyName = (user?.company?.name || 'Lojas').replace(/\s/g, '-')

        const getDatePart = () => {
          if (selectionType === 'month') {
            return getMonthName(start)
          }

          const fromDt = DateTime.fromJSDate(start)
          const toDt = DateTime.fromJSDate(end)
          const dateFormat = 'dd-MM-yyyy'

          if (fromDt.hasSame(toDt, 'day')) {
            return fromDt.toFormat(dateFormat)
          }

          return `${fromDt.toFormat(dateFormat)}_${toDt.toFormat(dateFormat)}`
        }

        const fileName = `Neopro - Relatorio-${companyName}-${getDatePart()}.xlsx`

        // ===== Aba Relatório diário =====
        // Uma linha por dia trabalhado, para cada loja e cada vendedor.
        // A Meta Diária usa o mesmo valor da aba "Metas diárias".
        const dailyReportHeaders = [
          'Loja',
          'Nome',
          'CPF',
          'Data',
          'Dias Trabalhados',
          'Faturamento',
          'Meta Diária',
          '% da Meta',
          'Meta atual',
          'Ticket Médio',
          'Preço Médio',
          'N˚ Peças',
          'N˚ Vendas'
        ]

        const formatReportDate = date =>
          DateTime.fromISO(date).toFormat('dd/MM/yyyy')

        const sortByDate = (a, b) => new Date(a.date) - new Date(b.date)

        const dailyReportData = [dailyReportHeaders]
        // Ordenação: por Loja, depois por dia. Em cada dia, a linha total
        // da loja fica no topo, seguida pelos vendedores daquele mesmo dia.
        storesReportData.forEach(({ name, store, sellers, hasInputs }) => {
          // Ignora lojas sem meta cadastrada no período
          if (!store) return

          // Pré-indexa dados de cada vendedor: dias com meta e dias de presença.
          // Presença = trabalho OU folga (dayOff); ausência = não estar no dia.
          const sellersData = (sellers || []).map(seller => ({
            name: seller.active
              ? seller.name.complete
              : `(INATIVO) ${seller.name.complete}`,
            cpf: seller.cpf ?? 'Não informado',
            hasInputs: seller.hasInputs !== false,
            presentSet: new Set(
              (seller.daysPresent || []).map(date => new Date(date).toISOString())
            ),
            daysByDate: (seller.daysGoal || []).reduce((acc, day) => {
              acc[new Date(day.date).toISOString()] = day
              return acc
            }, {})
          }))

          const storeDays = (store.daysGoal || []).slice().sort(sortByDate)

          storeDays.forEach(storeDay => {
            const dateKey = new Date(storeDay.date).toISOString()
            const storeGoal = roundSpreadsheetValue(storeDay.goal ?? 0)
            const storeSold = roundSpreadsheetValue(storeDay.sold ?? 0)
            const storeSales = storeDay.sales ?? 0
            const storeItems = storeDay.items ?? 0

            // Linha total da loja para o dia (1 se a loja abriu, senão 0)
            dailyReportData.push([
              name, // Loja
              'Loja', // Nome
              '-', // CPF
              formatReportDate(storeDay.date), // Data
              storeDay.working ? 1 : 0, // Dias Trabalhados
              salesDataDisabled ? '-' : storeSold, // Faturamento
              storeGoal, // Meta Diária
              salesDataDisabled ? '-' : safeDivision(storeSold, storeGoal), // % da Meta
              '-', // Meta atual
              salesDataDisabled || hasInputs === false
                ? '-'
                : safeDivision(storeSold, storeSales), // Ticket Médio
              salesDataDisabled || hasInputs === false
                ? '-'
                : safeDivision(storeSold, storeItems), // Preço Médio
              salesDataDisabled ? '-' : storeItems, // N˚ Peças
              salesDataDisabled ? '-' : storeSales // N˚ Vendas
            ])

            // Uma linha por vendedor no dia. Dias Trabalhados = 1 se presente
            // (trabalho ou folga), 0 se o vendedor não estava no dia.
            sellersData.forEach(seller => {
              const day = seller.daysByDate[dateKey]
              const present = seller.presentSet.has(dateKey)

              const goal = roundSpreadsheetValue(day?.goal ?? 0)
              const sold = roundSpreadsheetValue(day?.sold ?? 0)
              const sales = day?.sales ?? 0
              const items = day?.items ?? 0
              dailyReportData.push([
                name, // Loja
                seller.name, // Nome
                seller.cpf, // CPF
                formatReportDate(storeDay.date), // Data
                present ? 1 : 0, // Dias Trabalhados
                salesDataDisabled ? '-' : sold, // Faturamento
                goal, // Meta Diária
                salesDataDisabled ? '-' : safeDivision(sold, goal), // % da Meta
                day?.goalName || '-', // Meta atual (atingimento do dia)
                salesDataDisabled || !seller.hasInputs
                  ? '-'
                  : safeDivision(sold, sales), // Ticket Médio
                salesDataDisabled || !seller.hasInputs
                  ? '-'
                  : safeDivision(sold, items), // Preço Médio
                salesDataDisabled ? '-' : items, // N˚ Peças
                salesDataDisabled ? '-' : sales // N˚ Vendas
              ])
            })

            // Linha "Outras vendas" do dia (vendas não atribuídas a vendedores).
            // Sempre exibida, mesmo que zerada.
            const dayOther = storeDay.other || { sold: 0, sales: 0, items: 0 }
            const otherSold = roundSpreadsheetValue(dayOther.sold ?? 0)
            const otherSales = dayOther.sales ?? 0
            const otherItems = dayOther.items ?? 0
            dailyReportData.push([
              name, // Loja
              'Outras vendas', // Nome
              '-', // CPF
              formatReportDate(storeDay.date), // Data
              '-', // Dias Trabalhados
              salesDataDisabled ? '-' : otherSold, // Faturamento
              '-', // Meta Diária
              '-', // % da Meta
              '-', // Meta atual
              salesDataDisabled || hasInputs === false
                ? '-'
                : safeDivision(otherSold, otherSales), // Ticket Médio
              salesDataDisabled || hasInputs === false
                ? '-'
                : safeDivision(otherSold, otherItems), // Preço Médio
              salesDataDisabled ? '-' : otherItems, // N˚ Peças
              salesDataDisabled ? '-' : otherSales // N˚ Vendas
            ])
          })
        })

        const dailyReportSheet = XLSX.utils.aoa_to_sheet(dailyReportData)

        for (let i = 2; i <= dailyReportData.length; i++) {
          if (dailyReportSheet[`F${i}`])
            dailyReportSheet[`F${i}`].z = '"R$ "#,##0.00' // Faturamento
          if (dailyReportSheet[`G${i}`])
            dailyReportSheet[`G${i}`].z = '"R$ "#,##0.00' // Meta Diária
          if (dailyReportSheet[`H${i}`]) dailyReportSheet[`H${i}`].z = '0.00%' // % da Meta
          if (dailyReportSheet[`J${i}`])
            dailyReportSheet[`J${i}`].z = '"R$ "#,##0.00' // Ticket Médio
          if (dailyReportSheet[`K${i}`])
            dailyReportSheet[`K${i}`].z = '"R$ "#,##0.00' // Preço Médio
          if (dailyReportSheet[`L${i}`]) dailyReportSheet[`L${i}`].z = '0' // N˚ Peças
          if (dailyReportSheet[`M${i}`]) dailyReportSheet[`M${i}`].z = '0' // N˚ Vendas
        }

        XLSX.utils.book_append_sheet(wb, dailyReportSheet, 'Relatório diario')

        const formatDailyGoalDate = date =>
          DateTime.fromISO(date, { locale: 'pt-BR' })
            .toFormat('dd/MMM')
            .toLowerCase()
            .replace('.', '')

        const dailyGoalsByStore = storesReportData
          .filter(store => store.store)
          .map(store => ({
            storeName: store.name,
            storeGoals: (store.store?.daysGoal || []).reduce((acc, day) => {
              acc[formatDailyGoalDate(day.date)] = parseFloat(
                (day.goal ?? 0).toFixed(2)
              )
              return acc
            }, {}),
            sellers: (store.sellers || [])
              .filter(seller => seller.daysGoal?.length > 0)
              .map(seller => ({
                sellerName: seller.active
                  ? seller.name.complete
                  : `(INATIVO) ${seller.name.complete}`,
                goals: seller.daysGoal.reduce((acc, day) => {
                  acc[formatDailyGoalDate(day.date)] = parseFloat(
                    (day.goal ?? 0).toFixed(2)
                  )
                  return acc
                }, {})
              }))
          }))

        const sortedDates = [
          ...new Set(
            dailyGoalsByStore.flatMap(({ storeGoals }) => Object.keys(storeGoals))
          )
        ].sort()
        const excelData = [['Loja', 'Nome', ...sortedDates]]
        const totalRow = [
          'Total',
          '',
          ...sortedDates.map(() => 0)
        ]

        dailyGoalsByStore.forEach(({ storeName, storeGoals, sellers }) => {
          const storeRow = [storeName, 'Loja']

          sortedDates.forEach((date, index) => {
            const value = storeGoals[date] ?? 0
            storeRow.push(value)
            totalRow[index + 2] += value
          })

          excelData.push(storeRow)

          sellers.forEach(({ sellerName, goals }) => {
            const sellerRow = [storeName, sellerName]

            sortedDates.forEach(date => {
              sellerRow.push(goals[date] ?? 0)
            })

            excelData.push(sellerRow)
          })
        })

        excelData.splice(1, 0, totalRow)

        const dailyGoalsSheet = XLSX.utils.aoa_to_sheet(excelData)

        XLSX.utils.book_append_sheet(wb, dailyGoalsSheet, 'Metas diárias')

        // Ordena as abas: Todas as lojas, [Regionais], Relatório diario, Metas diárias, [lojas...]
        const baseSpecialIndex = groupsReportData.length > 0 ? 2 : 1

        const dailyReportIndex = wb.SheetNames.indexOf('Relatório diario')
        wb.SheetNames.splice(dailyReportIndex, 1)
        wb.SheetNames.splice(baseSpecialIndex, 0, 'Relatório diario')

        const allStoresColumnIndexx = wb.SheetNames.indexOf('Metas diárias')
        wb.SheetNames.splice(allStoresColumnIndexx, 1)
        wb.SheetNames.splice(baseSpecialIndex + 1, 0, 'Metas diárias')

        XLSX.writeFile(
          wb,
          fileName,
          {
            bookType: 'xlsx',
            type: 'binary'
          }
        )
        setIsLoading(false)
      } catch (err) {
        console.log(err)
        toast.error('Houve um erro ao tentar gerar a planilha')
        setIsLoading(false)
      }
  }

  const handleDayClick = calendarRange => {
    setRange(calendarRange)
  }

  return (
    <SheetModal open={isOpen} onClose={closeModal}>
      <div className='content'>
        <h1>Qual período você deseja exportar?</h1>
        <span>Selecione abaixo o período desejado</span>

        <div className='close-button' onClick={closeModal}>
          <CloseTwoTone style={{ color: 'lightgrey' }} />
        </div>

        <div className='checkbox-container'>
          <FormControl>
            <RadioGroup
              row
              value={selectionType}
              onChange={e => {
                const newSelectionType = e.target.value
                setSelectionType(newSelectionType)
                setSelectedMonth('')

                if (newSelectionType === 'range') {
                  const today = new Date()
                  const from = startOfMonth(today)
                  const to = today

                  setRange({
                    from,
                    to
                  })
                } else {
                  setRange({ from: null, to: null })
                }
              }}
            >
              <FormControlLabel
                value='month'
                control={<Radio />}
                label='Mês fechado'
              />
              <FormControlLabel
                value='range'
                control={<Radio />}
                label='Período específico'
              />
            </RadioGroup>
          </FormControl>
        </div>
        <div className='checkbox-container' style={{ marginTop: 0, minHeight: '60px' }}>
          {selectionType === 'month' ? (
            <FormControl>
              <InputLabel>Mês</InputLabel>
              <Select
                value={selectedMonth}
                onChange={event => setSelectedMonth(`${event.target.value}`)}
                MenuProps={MenuProps}
                style={{
                  width: '150px'
                }}
              >
                {months.map(month => {
                  const monthName = `${getMonthName(month)} / ${getYear(
                    new Date(month)
                  )}`
                  return (
                    <MenuItem key={month} value={month}>
                      <ListItemText primary={monthName} />
                    </MenuItem>
                  )
                })}
              </Select>
            </FormControl>
          ) : (
            <Grid container direction='column' style={{ position: 'relative' }}>
              <Grid
                item
                onClick={() => {
                  setIsCalendarPickerOpen(true)
                }}
                style={{
                  border: '1px solid #ccc',
                  padding: '8px',
                  borderRadius: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  cursor: 'pointer'
                }}
              >
                <DateRange style={{ width: 16, marginRight: 5 }} />
                <Typography>
                  {range.from
                    ? DateTime.fromJSDate(range.from).toFormat('dd/MM/yyyy')
                    : '-'}
                </Typography>
                <Typography style={{ margin: '0 5px' }}>até</Typography>
                <Typography>
                  {range.to
                    ? DateTime.fromJSDate(range.to).toFormat('dd/MM/yyyy')
                    : '-'}
                </Typography>
              </Grid>
              <Grid item>
                <div style={{ position: 'absolute', zIndex: 1, top: '40px' }}>
                  <DateRangePickerWrapper
                    open={isCalendarPickerOpen}
                    toggle={() => setIsCalendarPickerOpen(false)}
                    initialDateRange={{
                      startDate: range.from,
                      endDate: range.to
                    }}
                    onChange={newRange => {
                      handleDayClick({
                        from: newRange.startDate,
                        to: newRange.endDate
                      })
                      if (newRange.startDate && newRange.endDate) {
                        setIsCalendarPickerOpen(false)
                      }
                    }}
                    oneCalendar
                    locale={ptBR}
                  />
                </div>
              </Grid>
            </Grid>
          )}
        </div>

        <SheetButton
          disabled={isLoading}
          onClick={handleExportAllSpreadSheet}
          isModalButton
        >
          {isLoading ? (
            <>
              <CircularProgress
                size={24}
                color='inherit'
                style={{ marginRight: 12 }}
              />
              Baixando....
            </>
          ) : (
            <>
              <CloudDownloadTwoTone style={{ marginRight: 12 }} />
              Fazer download da planilha
            </>
          )}
        </SheetButton>
      </div>
    </SheetModal>
  )
}
